import { CustomColumnDef } from '@/types/table';
import { isStickyLeft } from './columnLayout';
import {
  ColumnDisplayItem,
  ColumnPinConfig,
  PersistedColumnConfig,
  MAX_PINNED_COLUMNS,
} from '@/types/table/columnConfig';

export interface MergedGroupInfo {
  childIds: string[];
}

export const getTableColumnId = <T>(column: CustomColumnDef<T>): string | null => column.id ?? null;

export const getTableColumnLabel = <T>(column: CustomColumnDef<T>): string => {
  if (typeof column.header === 'string') {
    return column.header;
  }

  return column.id ?? '';
};

export const getVisibleColumnCount = (visibility: Record<string, boolean>): number => {
  return Object.values(visibility).filter(Boolean).length;
};

export const buildDefaultColumnConfig = <T>(
  columns: CustomColumnDef<T>[],
  additionalHeaderLabels?: Record<string, string>,
): PersistedColumnConfig => {
  const visibility: Record<string, boolean> = {};
  const pinning: ColumnPinConfig[] = [];

  for (const column of columns) {
    const columnId = getTableColumnId(column);

    if (!columnId) {
      continue;
    }

    visibility[columnId] = column.defaultHidden ? false : column.visible !== false;

    if (!isStickyLeft(column.sticky)) {
      continue;
    }

    const pinEntry: ColumnPinConfig = { columnId };

    if (column.mergedGroup) {
      pinEntry.parentLabel = column.mergedGroup;
      pinEntry.childLabel = additionalHeaderLabels?.[columnId] || getTableColumnLabel(column);
    }

    pinning.push(pinEntry);
  }

  return { visibility, pinning };
};

export const applyColumnConfig = <T>(
  columns: CustomColumnDef<T>[],
  config: PersistedColumnConfig,
): CustomColumnDef<T>[] => {
  const columnMap = new Map(columns.map(column => [column.id, column]));
  const pinnedIdSet = new Set(config.pinning.map(pinEntry => pinEntry.columnId));

  const pinnedColumns = config.pinning.reduce<CustomColumnDef<T>[]>((configuredPinnedColumns, pinEntry) => {
    const column = columnMap.get(pinEntry.columnId);

    if (!column) {
      return configuredPinnedColumns;
    }

    configuredPinnedColumns.push({
      ...column,
      visible: config.visibility[pinEntry.columnId] ?? column.visible !== false,
      sticky: column.sticky || true,
    });

    return configuredPinnedColumns;
  }, []);

  const unpinnedColumns: CustomColumnDef<T>[] = [];
  const rightStickyColumns: CustomColumnDef<T>[] = [];

  for (const column of columns) {
    const columnId = column.id ?? '';
    const visible = config.visibility[columnId] ?? column.visible !== false;

    if (column.sticky === 'right') {
      rightStickyColumns.push({ ...column, visible });
    } else if (!pinnedIdSet.has(columnId)) {
      unpinnedColumns.push({ ...column, visible, sticky: false });
    }
  }

  return [...pinnedColumns, ...unpinnedColumns, ...rightStickyColumns];
};

export const arePinConfigsEqual = (left: ColumnPinConfig[], right: ColumnPinConfig[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((leftPin, index) => {
    const rightPin = right[index];

    return (
      leftPin.columnId === rightPin?.columnId &&
      leftPin.parentLabel === rightPin?.parentLabel &&
      leftPin.childLabel === rightPin?.childLabel
    );
  });
};

export const areVisibilityConfigsEqual = (left: Record<string, boolean>, right: Record<string, boolean>): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every(key => left[key] === right[key]);
};

export const areColumnConfigsEqual = (left: PersistedColumnConfig, right: PersistedColumnConfig): boolean => {
  return (
    areVisibilityConfigsEqual(left.visibility, right.visibility) && arePinConfigsEqual(left.pinning, right.pinning)
  );
};

export const buildMergedGroups = <T>(columns: CustomColumnDef<T>[]): Map<string, MergedGroupInfo> => {
  const groups = new Map<string, MergedGroupInfo>();

  for (const column of columns) {
    const columnId = getTableColumnId(column);

    if (!column.mergedGroup || !columnId) {
      continue;
    }

    const existingGroup = groups.get(column.mergedGroup);

    if (existingGroup) {
      existingGroup.childIds.push(columnId);
      continue;
    }

    groups.set(column.mergedGroup, { childIds: [columnId] });
  }

  return groups;
};

export const buildColumnMap = <T>(columns: CustomColumnDef<T>[]): Map<string, CustomColumnDef<T>> => {
  const columnMap = new Map<string, CustomColumnDef<T>>();

  for (const column of columns) {
    const columnId = getTableColumnId(column);

    if (columnId) {
      columnMap.set(columnId, column);
    }
  }

  return columnMap;
};

export const getOrderedColumnIds = <T>(columns: CustomColumnDef<T>[]): string[] => {
  return columns.map(getTableColumnId).filter((columnId): columnId is string => Boolean(columnId));
};

export const resolveColumnLabel = <T>(
  columnId: string,
  column: CustomColumnDef<T> | undefined,
  additionalHeaderLabels?: Record<string, string>,
): string => {
  const overrideLabel = additionalHeaderLabels?.[columnId];
  const headerLabel = column ? getTableColumnLabel(column) : '';
  const columnConfigLabel = column?.columnConfigLabel;

  // In a merged group the column header is the shared group title, so prefer the
  // additional header (column subtitle) to avoid duplicating the group title.
  if (column?.mergedGroup) {
    return overrideLabel || columnConfigLabel || headerLabel || columnId;
  }

  return columnConfigLabel || headerLabel || overrideLabel || columnId;
};

export const buildDisplayItem = <T>(
  columnId: string,
  draftConfig: PersistedColumnConfig,
  columnMap: Map<string, CustomColumnDef<T>>,
  pinnedIdSet: Set<string>,
  visibleCount: number,
  additionalHeaderLabels?: Record<string, string>,
): ColumnDisplayItem => {
  const column = columnMap.get(columnId);
  const isVisible = draftConfig.visibility[columnId] ?? true;

  return {
    id: columnId,
    label: resolveColumnLabel(columnId, column, additionalHeaderLabels),
    isVisible,
    isPinned: pinnedIdSet.has(columnId),
    isDisabled: isVisible && visibleCount <= 1,
    parentGroup: column?.mergedGroup,
    section: column?.columnConfigSection,
  };
};

export const buildPinnedItems = (
  pinning: ColumnPinConfig[],
  buildDisplayItemForColumn: (columnId: string) => ColumnDisplayItem,
): ColumnDisplayItem[] => {
  return pinning.map(pinEntry => {
    const displayItem = buildDisplayItemForColumn(pinEntry.columnId);

    if (pinEntry.parentLabel && pinEntry.childLabel) {
      return { ...displayItem, label: `${pinEntry.parentLabel}: ${pinEntry.childLabel}` };
    }

    return displayItem;
  });
};

export const toggleVisibilityInConfig = (
  previousConfig: PersistedColumnConfig,
  columnId: string,
  mergedGroups: Map<string, MergedGroupInfo>,
): PersistedColumnConfig => {
  const mergedGroup = mergedGroups.get(columnId);

  if (mergedGroup) {
    const nextVisibility = { ...previousConfig.visibility };
    const nextValue = !mergedGroup.childIds.every(childColumnId => nextVisibility[childColumnId] ?? true);

    for (const childColumnId of mergedGroup.childIds) {
      nextVisibility[childColumnId] = nextValue;
    }

    return getVisibleColumnCount(nextVisibility) > 0
      ? { ...previousConfig, visibility: nextVisibility }
      : previousConfig;
  }

  const currentlyVisible = previousConfig.visibility[columnId] ?? true;

  if (currentlyVisible && getVisibleColumnCount(previousConfig.visibility) <= 1) {
    return previousConfig;
  }

  return {
    ...previousConfig,
    visibility: { ...previousConfig.visibility, [columnId]: !currentlyVisible },
  };
};

export const countPinnedUnits = (pinning: ColumnPinConfig[]): number => {
  const pinnedGroupLabels = new Set<string>();
  let standalonePinnedCount = 0;

  for (const pinEntry of pinning) {
    if (pinEntry.parentLabel) {
      pinnedGroupLabels.add(pinEntry.parentLabel);
    } else {
      standalonePinnedCount += 1;
    }
  }

  return pinnedGroupLabels.size + standalonePinnedCount;
};

export const togglePinInConfig = <T>(
  previousConfig: PersistedColumnConfig,
  columnId: string,
  columnMap: Map<string, CustomColumnDef<T>>,
  additionalHeaderLabels?: Record<string, string>,
): PersistedColumnConfig => {
  const existingPinIndex = previousConfig.pinning.findIndex(pinEntry => pinEntry.columnId === columnId);

  if (existingPinIndex >= 0) {
    return {
      ...previousConfig,
      pinning: previousConfig.pinning.filter((_pinEntry, pinIndex) => pinIndex !== existingPinIndex),
    };
  }

  if (countPinnedUnits(previousConfig.pinning) >= MAX_PINNED_COLUMNS) {
    return previousConfig;
  }

  const column = columnMap.get(columnId);
  const nextPin: ColumnPinConfig = { columnId };

  if (column?.mergedGroup) {
    nextPin.parentLabel = column.mergedGroup;
    nextPin.childLabel = additionalHeaderLabels?.[columnId] || getTableColumnLabel(column);
  }

  return {
    ...previousConfig,
    pinning: [...previousConfig.pinning, nextPin],
  };
};

export const togglePinGroupInConfig = <T>(
  previousConfig: PersistedColumnConfig,
  columnIds: string[],
  columnMap: Map<string, CustomColumnDef<T>>,
  additionalHeaderLabels?: Record<string, string>,
): PersistedColumnConfig => {
  const pinnedColumnIdSet = new Set(previousConfig.pinning.map(pinEntry => pinEntry.columnId));
  const areAllColumnsPinned = columnIds.every(columnId => pinnedColumnIdSet.has(columnId));

  if (areAllColumnsPinned) {
    const columnIdsToUnpin = new Set(columnIds);

    return {
      ...previousConfig,
      pinning: previousConfig.pinning.filter(pinEntry => !columnIdsToUnpin.has(pinEntry.columnId)),
    };
  }

  const columnIdsToPin = columnIds.filter(columnId => !pinnedColumnIdSet.has(columnId));

  const newPinEntries = columnIdsToPin.map(columnId => {
    const column = columnMap.get(columnId);
    const pinEntry: ColumnPinConfig = { columnId };

    if (column?.mergedGroup) {
      pinEntry.parentLabel = column.mergedGroup;
      pinEntry.childLabel = additionalHeaderLabels?.[columnId] || getTableColumnLabel(column);
    }

    return pinEntry;
  });

  const nextPinning = [...previousConfig.pinning, ...newPinEntries];

  if (countPinnedUnits(nextPinning) > MAX_PINNED_COLUMNS) {
    return previousConfig;
  }

  return {
    ...previousConfig,
    pinning: nextPinning,
  };
};
